// The connected wallet as the SDK's: it signs the exact typed-data JSON the SDK built, through
// the wallet's own `eth_signTypedData_v4`, after switching to the chain its domain names. The SDK
// recovers every signature against the owner before anything is posted.

import { getConnection, switchChain } from "wagmi/actions";
import type { EIP1193Provider } from "viem";
import type { SdkWallet } from "../sdk";
import { createStore } from "../state/store";
import { wagmiConfig, type ChainId } from "./config";

/** What the wallet is being asked right now, for the page's confirm. */
export type Signing = { purpose: string; step: number; steps: number } | null;
export const signing = createStore<Signing>(null);

type Rejection = { kind: "declined" } | { kind: "wrongChain"; got: string; want: string } | { kind: "failed"; message: string };

const rejected = (error: unknown) => {
  const code = (error as { code?: unknown; cause?: { code?: unknown } })?.code ?? (error as { cause?: { code?: unknown } })?.cause?.code;
  const name = (error as { name?: unknown })?.name;
  return code === 4001 || name === "UserRejectedRequestError";
};

const words = (error: unknown) => (error instanceof Error ? error.message : String(error));

/** The wallet at `address`, for `steps` signatures (a deposit asks for two). */
export function walletFor(address: string, steps = 1): SdkWallet {
  const owner = address.toLowerCase();
  let step = 0;
  return {
    address: owner,
    async sign(purpose, typedData) {
      step += 1;
      signing.set({ purpose, step, steps: Math.max(steps, step) });
      try {
        const connection = getConnection(wagmiConfig);
        if (!connection.connector || connection.address?.toLowerCase() !== owner) {
          throw { kind: "failed", message: "the wallet changed: connect the account's owner" } satisfies Rejection;
        }
        const want = Number((JSON.parse(typedData) as { domain?: { chainId?: number | string } }).domain?.chainId);
        if (connection.chainId !== want) {
          try {
            await switchChain(wagmiConfig, { chainId: want as ChainId });
          } catch (error) {
            if (rejected(error)) throw { kind: "declined" } satisfies Rejection;
            throw { kind: "wrongChain", got: String(connection.chainId ?? ""), want: String(want) } satisfies Rejection;
          }
        }
        const provider = (await connection.connector.getProvider()) as EIP1193Provider;
        try {
          const signature = await provider.request({
            method: "eth_signTypedData_v4",
            params: [owner as `0x${string}`, typedData],
          });
          return signature as string;
        } catch (error) {
          if (rejected(error)) throw { kind: "declined" } satisfies Rejection;
          throw { kind: "failed", message: words(error) } satisfies Rejection;
        }
      } finally {
        signing.set(null);
      }
    },
  };
}
